import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import { selectAIMessages } from '@store/reducers/ai'
import type {
  AIChatMessageUI,
  AiChatStateResponse,
  AiRemainingRequestsResponse,
  AiTaskMessage,
} from '@store/reducers/ai/types'
import { artBoardActions } from '@store/reducers/artboard'
import { saveProjectSaga } from '@store/reducers/manager/sagas'
import axios, { type AxiosResponse } from 'axios'
import type { SagaIterator } from 'redux-saga'
import { call, put, select } from 'redux-saga/effects'

import { AIExternalActions, AIInternalActions } from '../actions'
import { AI_CHAT_BASE_URL, fetchAIChatState } from './performRequest'
import { type AiClarifyData, getClarifyDataFromTask } from './taskFlow'

export const getAiRemainingRequests = (
  projectId: string
): Promise<AxiosResponse<AiRemainingRequestsResponse>> =>
  axios.get(`/remaining/${projectId}`, {
    baseURL: AI_CHAT_BASE_URL,
    headers: {
      'X-Auth-Token': localStorage.getItem('token'),
    },
  })

const isChatRole = (role: string): role is 'assistant' | 'user' =>
  role === 'assistant' || role === 'user'

const toAIChatMessages = (messages: AiTaskMessage[]): AIChatMessageUI[] =>
  messages.reduce<AIChatMessageUI[]>((result, { role, content }) => {
    if (!isChatRole(role) || content.trim().length === 0) {
      return result
    }

    result.push({
      type: role,
      text: content,
    })

    return result
  }, [])

const withClarifyMessage = (
  messages: AIChatMessageUI[],
  clarifyData: AiClarifyData
): AIChatMessageUI[] => {
  const lastMessage = messages[messages.length - 1]
  const baseMessages =
    lastMessage?.type === 'assistant' ? messages.slice(0, -1) : messages

  return [
    ...baseMessages,
    {
      type: 'clarify',
      text: clarifyData.question,
      options: clarifyData.options,
    },
  ]
}

export function* startChat({
  payload,
}: ReturnType<typeof AIExternalActions.startChat>): SagaIterator {
  const { message, keepSidebarClosed = false } = payload

  let projectId = yield select(migrationProjectSelector.getProjectId)

  // Open the chat immediately so the panel appears on click; the skeleton is
  // shown in the messages area while the history request is in flight.
  // `keepSidebarClosed` leaves the current state alone instead of forcing the
  // panel closed, so a chat the user opened by hand stays open.
  if (!keepSidebarClosed) {
    yield put(
      artBoardActions.showAiChat({
        showAiChat: true,
      })
    )
  }

  // The AI chat service requires a projectId on every /chat request, so an
  // unsaved project has to be persisted before the first message. Save it in
  // the background with `ignoreAuthenticated` so we never prompt the user to
  // log in / register just for opening the chat. Skipped when a message is
  // passed in (generate flow), which already triggers the save itself.
  if (!projectId && !message) {
    yield call(saveProjectSaga, { payload: { ignoreAuthenticated: true } })
    projectId = yield select(migrationProjectSelector.getProjectId)
  }

  if (message) {
    yield put(
      AIExternalActions.addMessage({
        message: {
          type: 'user',
          text: message,
          isDescription: true,
        },
      })
    )
  }

  if (!message && projectId) {
    yield put(AIInternalActions.setHistoryLoading(true))

    try {
      const chatStateResponse: AxiosResponse<AiChatStateResponse> = yield call(
        fetchAIChatState,
        projectId
      )

      const { messages: historyMessages, activeTask } = chatStateResponse.data
      const historyChatMessages = toAIChatMessages(historyMessages)

      const clarifyData =
        activeTask?.status === 'AWAITING_USER'
          ? getClarifyDataFromTask(activeTask)
          : null

      const chatMessages = clarifyData
        ? withClarifyMessage(historyChatMessages, clarifyData)
        : historyChatMessages

      yield put(AIInternalActions.setMessages(chatMessages))
    } catch {
      // Keep local state if chat history request fails.
    } finally {
      yield put(AIInternalActions.setHistoryLoading(false))
    }
  }

  if (!message) {
    const messages: ReturnType<typeof selectAIMessages> =
      yield select(selectAIMessages)

    if (messages.length === 0) {
      yield put(
        AIExternalActions.addMessage({
          message: {
            type: 'assistant',
            text: `Hi there! I'm your friendly AI assistant, currently in Beta version and still learning. How can I help you with your project?`,
          },
        })
      )
    }
  }

  if (projectId) {
    const response: AxiosResponse<AiRemainingRequestsResponse> = yield call(
      getAiRemainingRequests,
      projectId
    )

    const { remainingRequests } = response.data

    yield put(AIInternalActions.updateRemainingRequests(remainingRequests))
  }
}
